<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendWebMessageMutation from "@api/mutations/sendWebMessage.graphql";
	import Formatted from "@components/common/Formatted.svelte";
	import Modal from "@components/common/Modal.svelte";
	import Input from "@components/form/Input.svelte";
	import Textarea from "@components/form/Textarea.svelte";
	import { pushGenerateLeadEvent } from "@utils/pushGenerateLeadEvent.js";
	import { on } from "svelte/events";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { Product } from "@core/schema/Product.js";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { formatPhoneNumberOnInput } from "@core/utils/svelte/formatPhoneNumberOnInput.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import Alert from "./Alert.svelte";
	import RecaptchaDisclaimer from "./RecaptchaDisclaimer.svelte";
	import Button from "./common/Button.svelte";
	import Form from "./form/Form.svelte";
	import LabelledInput from "./form/LabelledInput.svelte";

	let {
		showInquiryForm = $bindable(false),
		/* eslint-disable prefer-const */
		productId,
		/* eslint-enable prefer-const */
	}: { productId?: Product["id"]; showInquiryForm?: boolean } = $props();

	let loading = $state(false);
	let isError = $state(false);
	let modalMessage = $state<string>();
	let form = $state<HTMLFormElement>();

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	async function onsubmit(e: SubmitEvent): Promise<void> {
		e.preventDefault();
		if (e.target !== form || !showInquiryForm) {
			return;
		}
		const formData = new FormData(form);
		loading = true;
		try {
			if (!navigator.onLine) {
				throw new Error("Nejste připojeni k internetu.");
			}
			const now = new Date().toISOString();
			await mutation<boolean, { input: WebMessageInput }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessageMutation, {
				input: {
					gdprApprovedAt: now,
					name: formData.get("name") as string,
					phoneNumber: formData.get("phoneNumber") as string,
					email: formData.get("email") as string,
					message: formData.get("message") as string,
					locale: tenant.locale,
					sentFromUrl: location.href,
					productId,
					type: productId ? WebMessageType.ProductDemand : WebMessageType.ContactThroughModal,
				},
			});
			isError = false;
			pushGenerateLeadEvent("inquiry");
			modalMessage = "Vaše zpráva byla úspěšně odeslána. Podrobnosti jsme Vám zaslali na Vaši e-mailovou adresu.";
			form.reset();
		} catch (err) {
			isError = true;
			modalMessage = `Stala se chyba. Zkuste, prosím, formulář odeslat později. Detail chyby: ${err instanceof Error ? err.message : String(err)}`;
		} finally {
			loading = false;
		}
	}
	function clearModalMessage(): void {
		window.requestAnimationFrame(() => {
			if (isError) {
				modalMessage = undefined;
			} else {
				showInquiryForm = false;
				modalMessage = undefined;
				form?.reset();
			}
		});
	}

	$effect(() => {
		if (!showInquiryForm) {
			modalMessage = undefined;
			form?.reset();
		}
	});

	$effect(() => form && on(form, "submit", (e) => void onsubmit(e)));
</script>

<Modal bind:show={showInquiryForm} class="w-[50rem] px-6 max-w-[calc(100vw-1rem)] overflow-x-hidden" noBackdrop>
	<div
		class="mb-4 font-druk-cond tracking-[0.02em] text-[3rem] sm:text-[4rem] uppercase text-cmcsecondary sm:absolute sm:top-6"
	>
		{#if productId}
			<Formatted text="Poptávkový formulář" />
		{:else}
			<Formatted text="Kontaktní formulář" />
		{/if}
	</div>
	<Form bind:form class="flex flex-col gap-4 relative sm:mt-8 pb-1 overflow-x-hidden">
		{#if modalMessage !== undefined}
			<Alert type={isError ? "warning" : "success"} class="absolute top-4 inset-x-0">
				<Formatted text={modalMessage} />
			</Alert>
		{/if}
		<Input
			required
			name="name"
			autocomplete="name"
			placeholder="Jméno a příjmení"
			class={[modalMessage !== undefined && "opacity-0 pointer-events-none -z-10"]}
			aria-hidden={modalMessage !== undefined}
			tabindex={modalMessage !== undefined ? -1 : 0}
		/>
		<Input
			required
			name="phoneNumber"
			type="tel"
			autocomplete="tel"
			placeholder="Telefon"
			class={[modalMessage !== undefined && "opacity-0 pointer-events-none -z-10"]}
			aria-hidden={modalMessage !== undefined}
			tabindex={modalMessage !== undefined ? -1 : 0}
			oninput={formatPhoneNumberOnInput}
			pattern="[\d\s\+\-]+"
			maxlength={20}
		/>
		<Input
			required
			name="email"
			type="email"
			autocomplete="email"
			placeholder="E-mail"
			class={[modalMessage !== undefined && "opacity-0 pointer-events-none -z-10"]}
			aria-hidden={modalMessage !== undefined}
			tabindex={modalMessage !== undefined ? -1 : 0}
		/>
		<!-- Key block rerenders the textarea whenever the dialog opens. This is required for autosize action to take place. -->
		{#key showInquiryForm}
			<Textarea
				required
				name="message"
				placeholder="Stačí, když nám ve formuláři krátce popíšete, s čím potřebujete pomoci, a my se co nevidět ozveme."
				class={[modalMessage !== undefined && "opacity-0 pointer-events-none -z-10"]}
				aria-hidden={modalMessage !== undefined}
				tabindex={modalMessage !== undefined ? -1 : 0}
			/>
		{/key}
		<div class="flex justify-between h-max">
			<div class={[modalMessage !== undefined && "opacity-0 pointer-events-none -z-10"]}>
				<LabelledInput
					type="checkbox"
					name="gdprApprovedAt"
					required
					aria-hidden={modalMessage !== undefined}
					tabindex={modalMessage !== undefined ? -1 : 0}
				>
					<Formatted text="Souhlasím se l[{getGdprPath(tenant)}][[zpracováním osobních údajů]]" />
				</LabelledInput>
			</div>
			<Button
				variant={ButtonVariant.Primary}
				icon={loading ? "loading" : modalMessage !== undefined ? "none" : "arrow"}
				type={modalMessage !== undefined ? "button" : "submit"}
				onclick={modalMessage !== undefined ? clearModalMessage : undefined}
				disabled={loading}
			>
				{#if modalMessage !== undefined}
					<Formatted text={isError ? "Zkusit znovu" : "OK"} />
				{:else if loading}
					<Formatted text="Odesílám" />
				{:else}
					<Formatted text="Odeslat" />
				{/if}
			</Button>
		</div>
		<RecaptchaDisclaimer />
	</Form>
</Modal>
